import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemAvatar,
  Avatar,
  CircularProgress,
  Alert,
  IconButton,
  Divider,
  TextField,
  InputAdornment,
  Chip
} from '@mui/material';
import {
  Phone as PhoneIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Person as PersonIcon,
  CloudOff as OfflineIcon,
  CloudDone as OnlineIcon
} from '@mui/icons-material';
import { getApiUrl } from '../utils/apiUrl';
import { DEMO_MODE, demoEmployees } from '../services/demoData';

const CONTACTS_CACHE_KEY = 'mobile_contacts_cache';
const CONTACTS_CACHE_TIMESTAMP_KEY = 'mobile_contacts_cache_timestamp';

const MobileContacts = () => {
  const API_URL = getApiUrl();
  const [employees, setEmployees] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [isOffline, setIsOffline] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState(null);

  // Load cached contacts from localStorage
  const loadCachedContacts = () => {
    try {
      const cached = localStorage.getItem(CONTACTS_CACHE_KEY);
      const timestamp = localStorage.getItem(CONTACTS_CACHE_TIMESTAMP_KEY);
      if (cached) {
        const parsedContacts = JSON.parse(cached);
        console.log('[MobileContacts] Loaded from cache:', parsedContacts.length, 'contacts');
        setEmployees(parsedContacts);
        if (timestamp) {
          setLastSyncTime(new Date(timestamp));
        }
        return parsedContacts;
      }
    } catch (err) {
      console.error('[MobileContacts] Error loading cache:', err);
    }
    return null;
  };

  // Save contacts to localStorage
  const saveContactsToCache = (contacts) => {
    try {
      localStorage.setItem(CONTACTS_CACHE_KEY, JSON.stringify(contacts));
      const now = new Date().toISOString();
      localStorage.setItem(CONTACTS_CACHE_TIMESTAMP_KEY, now);
      setLastSyncTime(new Date(now));
      console.log('[MobileContacts] Saved to cache:', contacts.length, 'contacts');
    } catch (err) {
      console.error('[MobileContacts] Error saving cache:', err);
    }
  };

  const getToken = () => localStorage.getItem('token');

  useEffect(() => {
    loadEmployees();
  }, []);

  useEffect(() => {
    filterEmployees();
  }, [searchQuery, employees]);

  const loadEmployees = async (forceRefresh = false) => {
    setError('');
    setIsOffline(false);
    
    // First, load from cache for instant display
    if (!forceRefresh) {
      const cached = loadCachedContacts();
      if (cached && cached.length > 0) {
        setLoading(false);
        // Continue to try syncing from server in background
      }
    }
    
    if (DEMO_MODE?.enabled) {
      setTimeout(() => {
        setEmployees(demoEmployees || []);
        setLoading(false);
      }, 500);
      return;
    }

    const shouldHideEmployee = (emp) => {
      const fullName = `${emp?.firstName || ''} ${emp?.lastName || ''}`
        .replace(/\s+/g, ' ')
        .trim()
        .toLowerCase();
      return fullName === 'main admin rias';
    };

    try {
      const response = await fetch(`${API_URL}/employees`, {
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        const visibleEmployees = data.filter(e => !shouldHideEmployee(e));
        console.log('[MobileContacts] Synced from server:', visibleEmployees.length, 'contacts');
        setEmployees(visibleEmployees);
        setIsOffline(false);
        
        // Save to cache for offline use
        saveContactsToCache(visibleEmployees);
      } else {
        // Server error - use cache if available
        const cached = loadCachedContacts();
        if (!cached || cached.length === 0) {
          setError('Kunne ikke laste ansatte');
        } else {
          setIsOffline(true);
        }
      }
    } catch (err) {
      console.error('[MobileContacts] Network error:', err);
      // Network error - use cache
      const cached = loadCachedContacts();
      if (!cached || cached.length === 0) {
        setError('Ingen internett-tilkobling og ingen lagrede kontakter');
      } else {
        setIsOffline(true);
        console.log('[MobileContacts] Using cached data (offline)');
      }
    } finally {
      setLoading(false);
    }
  };

  const filterEmployees = () => {
    if (!searchQuery.trim()) {
      setFilteredEmployees(employees);
      return;
    }

    const query = searchQuery.toLowerCase();
    const filtered = employees.filter(emp =>
      `${emp.firstName} ${emp.lastName}`.toLowerCase().includes(query) ||
      emp.email?.toLowerCase().includes(query) ||
      emp.phone?.includes(query) ||
      emp.roleName?.toLowerCase().includes(query)
    );
    setFilteredEmployees(filtered);
  };

  const handleCall = (phone) => {
    if (phone) {
      window.location.href = `tel:${phone}`;
    }
  };

  const getInitials = (firstName, lastName) => {
    return `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`.toUpperCase();
  };

  const getAvatarColor = (name) => {
    const colors = ['#1976d2', '#388e3c', '#d32f2f', '#f57c00', '#7b1fa2', '#0097a7'];
    const index = (name?.charCodeAt(0) || 0) % colors.length;
    return colors[index];
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h5" fontWeight="bold">
          Kontakter
        </Typography>
        <IconButton onClick={() => loadEmployees(true)} color="primary" disabled={loading}>
          <RefreshIcon />
        </IconButton>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Search */}
      <TextField
        fullWidth
        placeholder="Søk etter navn, telefon eller rolle..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        sx={{ mb: 2 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
      />

      {/* Offline indicator */}
      {isOffline && (
        <Alert 
          severity="warning" 
          icon={<OfflineIcon />}
          sx={{ mb: 2 }}
        >
          <Typography variant="body2" fontWeight="bold">
            Offline modus
          </Typography>
          <Typography variant="caption">
            Viser lagrede kontakter. {lastSyncTime && `Sist oppdatert: ${lastSyncTime.toLocaleString('nb-NO')}`}
          </Typography>
        </Alert>
      )}

      {/* Stats */}
      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <Chip 
          icon={<PersonIcon />}
          label={`${employees.length} ansatte${searchQuery ? ` (${filteredEmployees.length} vises)` : ''}`}
          color="primary"
          variant="outlined"
        />
        <Chip 
          icon={<PhoneIcon />}
          label={`${employees.filter(e => e.phone).length} med telefon`}
          color="success"
          variant="outlined"
        />
        {!isOffline && lastSyncTime && (
          <Chip 
            icon={<OnlineIcon />}
            label="Synkronisert"
            color="success"
            size="small"
          />
        )}
      </Box>

      {/* Employee List */}
      {filteredEmployees.length === 0 ? (
        <Card>
          <CardContent>
            <Typography color="text.secondary" align="center">
              {searchQuery ? 'Ingen ansatte funnet' : 'Ingen ansatte registrert'}
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <List sx={{ p: 0 }}>
            {filteredEmployees.map((employee, index) => (
              <React.Fragment key={employee.id}>
                <ListItem
                  sx={{
                    py: 2,
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                  }}
                >
                  <Box sx={{ display: 'flex', width: '100%', mb: 1 }}>
                    <ListItemAvatar>
                      <Avatar
                        sx={{
                          bgcolor: getAvatarColor(employee.firstName),
                          width: 50,
                          height: 50,
                          fontSize: '1.2rem',
                        }}
                      >
                        {getInitials(employee.firstName, employee.lastName)}
                      </Avatar>
                    </ListItemAvatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="subtitle1" fontWeight="bold" noWrap>
                        {employee.firstName} {employee.lastName}
                      </Typography>
                      {employee.roleName && (
                        <Chip
                          label={employee.roleName}
                          size="small"
                          sx={{ mt: 0.5 }}
                        />
                      )}
                    </Box>
                  </Box>

                  {/* Contact Info */}
                  <Box sx={{ width: '100%', ml: 7 }}>
                    {employee.phone && (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          mb: 1,
                          cursor: 'pointer',
                          '&:hover': { color: 'primary.main' },
                        }}
                        onClick={() => handleCall(employee.phone)}
                      >
                        <PhoneIcon fontSize="small" color="action" />
                        <Typography variant="body2" color="primary">
                          {employee.phone}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          (Trykk for å ringe)
                        </Typography>
                      </Box>
                    )}
                    {!employee.phone && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <PhoneIcon fontSize="small" color="disabled" />
                        <Typography variant="body2" color="text.secondary" fontStyle="italic">
                          Ingen telefon
                        </Typography>
                      </Box>
                    )}
                    
                  </Box>
                </ListItem>
                {index < filteredEmployees.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </Card>
      )}
    </Box>
  );
};

export default MobileContacts;
